import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import CaseAutocomplete from "components/CaseAutocomplete";
import DebouncedText from "components/DebouncedText";
import {
  CaseExportCustomTemplateJsonDocument,
  Cell,
  CellType,
  DownloadCustomTemplateDocument,
  FieldExportLayout,
  FieldExportTagFilter,
  FieldExportValueFilter,
  FieldTagConfigView,
  FormField,
  ProfileExportConfigInput,
  ProfileExportConfigView,
  TagView,
  type SearchCasesResult,
} from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import produce from "immer";
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  createProfileExportConfig,
  deleteProfileExportConfig,
  selectFieldTagConfigs,
  selectProfileExportConfigs,
  selectTags,
  updateProfileExportConfig,
} from "redux/slices/clientLens";
import { fetchForm, selectForm, selectFormFields } from "redux/slices/form.slice";
import { StateHandle } from "StateHandle";
import { P } from "theme/typography";
import CaseFormProvider from "../CaseFormProvider";
import ProfileExportConfigPreview from "./ProfileExportPreview";
import { FieldExportConfigUI, ProfileExportConfigUI } from "routes/lenses/lens/case/exports/ExportConfigInterfaces";
import { Clear, ContentCopy, Download, FileUpload } from "@mui/icons-material";
import { backend, parseSafeInt } from "Backend";
import { useSnackbar } from "notistack";
import { LabeledBorderBox } from "routes/lenses/lens/case/LabeledBorderBox";
import truncateString from "helpers/truncateString";
import { Document, Page } from "react-pdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import { selectIsOwnerOrSupport } from "redux/slices/session";
import { AxiosError } from "axios";
import { runGqlQuery } from "gqlClient";
import useCopy from "helpers/hooks/useCopy";
import triggerDownload, { triggerDownloadWithUrl } from "helpers/triggerDownload";
import CollapsibleSections from "components/CollapsibleSections";
import { HelpIconOutlined } from "components/CustomIcons";
import { STARRED_ENTRY, STARRED_TAG } from "components/tags/constants";
import TagSelect from "components/tags/TagSelect";
import { useNavigate } from "react-router";
import { AppRoutes } from "AppRoutes";

interface ProfileExportConfigBuilderProps {
  lensId: number;
  configId?: number;
}

interface FieldConfigProps {
  fieldConfig: FieldExportConfigUI;
  formField: FormField;
  fieldTagConfig: FieldTagConfigView | undefined;
  tags: TagView[];
  profileExportConfigHandle: StateHandle<ProfileExportConfigUI>;
}

const ExportConfigCheckboxLabels = {
  useCustomTemplate: "Include Custom Template",
  skipCoverPages: "Include Standard Profile Details",
  standardProfileOptions: {
    includeAnnotations: "Include Annotations and Highlights",
    includeNoResponse: "Show Fields with No Responses",
    includeSummary: "Include Summary",
  },
  includeAllPages: "Always Include Entire Document",
  skipSupportingRecords: "Supporting Documents",
};

const newProfileExportConfig = (lensId: number, formFields: FormField[]): ProfileExportConfigUI => ({
  lensId,
  name: "",
  fieldConfigs: formFields.map((ff) => ({
    fieldId: ff.id,
    cellIds: ff.cells.filter((c) => !c.exportExclude).map((c) => c.id),
    skipNotes: false,
    layout: FieldExportLayout.Table,
    included: true,
  })),
  noResponseText: "No Response",
});

const profileExportConfigViewToProfileExportConfigUI = (
  config: ProfileExportConfigView,
  formFields: FormField[]
): ProfileExportConfigUI => {
  return {
    ...config.config,
    lensId: config.lensId,
    name: config.name,
    fieldConfigs: formFields.map((ff) => {
      const fieldConfig = (config.config.fieldConfigs || []).find((fc) => fc.fieldId === ff.id);

      if (fieldConfig) {
        return {
          ...fieldConfig,
          cellIds:
            fieldConfig.cellIds && fieldConfig.cellIds.length > 0 ? fieldConfig.cellIds : ff.cells.map((c) => c.id),
          included: true,
        };
      } else {
        return {
          cellIds: ff.cells.map((c) => c.id),
          fieldId: ff.id,
          layout: FieldExportLayout.Table,
          included: false,
          skipNotes: false,
        };
      }
    }),
  };
};

const profileExportConfigUIToProfileExportConfigInput = (
  config: ProfileExportConfigUI,
  formFields: FormField[]
): ProfileExportConfigInput => {
  return {
    fieldConfigs: (config.fieldConfigs || [])
      .filter((fc) => fc.included)
      .map((fc) => {
        const foundFormField = formFields.find((ff) => ff.id === fc.fieldId);

        if (foundFormField) {
          return {
            fieldId: fc.fieldId,
            cellIds: fc.cellIds && fc.cellIds.length === foundFormField.cells.length ? [] : fc.cellIds,
            skipNotes: fc.skipNotes,
            layout: fc.layout,
            selectedOnly: fc.selectedOnly,
            contextPageRange: fc.contextPageRange === 0 ? null : fc.contextPageRange,
            valueFilter: fc.valueFilter,
            tagFilter: fc.tagFilter,
          };
        } else {
          throw new Error("Cannot create a field config for a non-existent form field");
        }
      }),
    skipAnnotations: config.skipAnnotations,
    skipSummary: config.skipSummary,
    skipNoResponse: config.skipNoResponse,
    skipCoverPages: config.skipCoverPages,
    title: config.title,
    noResponseText: config.noResponseText,
    templateConfig: config.templateConfig,
    skipSupportingRecords: config.skipSupportingRecords,
    includeAllPages: config.includeAllPages,
  };
};

interface ProfileConfigProps {
  profileExportConfigHandle: StateHandle<ProfileExportConfigUI>;
  templateConfigFileHandle: StateHandle<File | undefined>;
  useCustomTemplateHandle: StateHandle<boolean>;
}

const ProfileConfig = ({
  profileExportConfigHandle,
  templateConfigFileHandle,
  useCustomTemplateHandle,
}: ProfileConfigProps) => {
  const [profileExportConfig, setProfileExportConfig] = profileExportConfigHandle;
  const [useCustomTemplate, setUseCustomTemplate] = useCustomTemplateHandle;
  const [templateConfigFile, setTemplateConfigFile] = templateConfigFileHandle;
  const { enqueueSnackbar } = useSnackbar();
  const isOwnerOrSupport = useAppSelector(selectIsOwnerOrSupport);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const onFileUploadClick = () => {
    // `current` points to the mounted file input element
    if (inputFileRef.current) inputFileRef.current.click();
  };

  const onFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const fileUploaded = evt.target.files?.[0];
    setTemplateConfigFile(fileUploaded);
    setProfileExportConfig(
      produce((draft) => {
        if (fileUploaded?.name) {
          draft.templateConfig = { fileName: fileUploaded?.name };
        } else {
          draft.templateConfig = undefined;
        }
      })
    );
    // We reset to allow the user to upload the same file again
    if (inputFileRef.current) inputFileRef.current.value = "";
  };

  const clearTemplateFile = () => {
    setTemplateConfigFile(undefined);
    setProfileExportConfig(
      produce((draft) => {
        draft.templateConfig = undefined;
      })
    );
  };

  const downloadCustomTemplate = async () => {
    const templateConfig = profileExportConfig.templateConfig;
    if (templateConfigFile && templateConfig) {
      triggerDownload(templateConfigFile, templateConfig.fileName);
    } else if (templateConfig && profileExportConfig.lensId) {
      const r = await runGqlQuery(DownloadCustomTemplateDocument, {
        lensId: profileExportConfig.lensId,
        templateFileName: templateConfig.fileName,
      });
      triggerDownloadWithUrl(r.lens.downloadCustomTemplate.url);
    } else {
      enqueueSnackbar("No template config file to download", { variant: "error" });
    }
  };

  const setSummary = (showSummary: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.skipSummary = !showSummary;
      })
    );
  };

  const setAnnotations = (showAnnotations: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.skipAnnotations = !showAnnotations;
      })
    );
  };

  const setNoResponse = (showNoResponse: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.skipNoResponse = !showNoResponse;
      })
    );
  };

  const setCoverPages = (showCoverPages: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.skipCoverPages = !showCoverPages;
      })
    );
    setSummary(false);
    setNoResponse(false);
  };

  const setName = (name: string) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.name = name;
      })
    );
  };

  const setTitle = (title: string) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.title = title;
      })
    );
  };

  const setNoResponseText = (text: string) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.noResponseText = text;
      })
    );
  };

  const setDocumentReferences = (showDocumentReferences: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.skipSupportingRecords = !showDocumentReferences;
      })
    );
  };

  const setIncludeEntireReferenceDocument = (includeEntireReferenceDocument: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        draft.includeAllPages = includeEntireReferenceDocument;
      })
    );
  };

  return (
    <div className="flex flex-col">
      <div className="mb-2">
        <DebouncedText
          value={profileExportConfig.name || ""}
          onChange={(text) => setName(text)}
          formatter={(text) => text.substring(0, 64)}
          label="Name"
          textFieldProps={{
            fullWidth: true,
            required: true,
          }}
        />
      </div>

      {isOwnerOrSupport && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCustomTemplate}
                onChange={(evt) => {
                  const selected = evt.target.checked;
                  setUseCustomTemplate(selected);
                  if (!selected) {
                    clearTemplateFile();
                  }
                }}
              />
            }
            label={ExportConfigCheckboxLabels.useCustomTemplate}
          />

          {useCustomTemplate && (
            <LabeledBorderBox label="Custom Template">
              <div className="flex flex-row items-center justify-between">
                <P variant="xsBold">Template File</P>
                {profileExportConfig.templateConfig?.fileName === undefined ? (
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    endIcon={<FileUpload />}
                    onClick={onFileUploadClick}
                    disableElevation={true}
                    sx={{
                      boxShadow: 0,
                    }}>
                    Choose file
                    <input
                      ref={inputFileRef}
                      type="file"
                      autoComplete="off"
                      tabIndex={-1}
                      accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      style={{ display: "none" }}
                      onChange={onFile}
                    />
                  </Button>
                ) : (
                  <div>
                    <IconButton size="small" onClick={downloadCustomTemplate}>
                      <Download />
                    </IconButton>
                    <P variant="xs">{truncateString(profileExportConfig.templateConfig.fileName, 30)}</P>
                    <IconButton size="small" onClick={clearTemplateFile}>
                      <Clear />
                    </IconButton>
                  </div>
                )}
              </div>
            </LabeledBorderBox>
          )}
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={!profileExportConfig.skipCoverPages}
            onChange={(evt) => setCoverPages(evt.target.checked)}
          />
        }
        label={ExportConfigCheckboxLabels.skipCoverPages}
      />
      {!profileExportConfig.skipCoverPages && (
        <LabeledBorderBox label="Standard Profile Details">
          {!profileExportConfig.skipCoverPages && (
            <>
              <div className="mt-1">
                <DebouncedText
                  value={profileExportConfig.title || ""}
                  formatter={(text) => text.substring(0, 64)}
                  onChange={(text) => setTitle(text)}
                  label="Title"
                />
              </div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!profileExportConfig.skipNoResponse}
                    onChange={(evt) => setNoResponse(evt.target.checked)}
                    disabled={!!profileExportConfig.skipCoverPages}
                  />
                }
                label={ExportConfigCheckboxLabels.standardProfileOptions.includeNoResponse}
              />
              {!profileExportConfig.skipNoResponse && (
                <DebouncedText
                  value={profileExportConfig.noResponseText || ""}
                  onChange={(text) => setNoResponseText(text)}
                  label="No Response Text"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!profileExportConfig.skipSummary}
                    onChange={(evt) => setSummary(evt.target.checked)}
                    disabled={!!profileExportConfig.skipCoverPages}
                  />
                }
                label={ExportConfigCheckboxLabels.standardProfileOptions.includeSummary}
              />
            </>
          )}
        </LabeledBorderBox>
      )}
      <div className="flex flex-row items-center mt-2">
        <FormControlLabel
          control={
            <Checkbox
              checked={!profileExportConfig.skipAnnotations}
              onChange={(evt) => setAnnotations(evt.target.checked)}
            />
          }
          label={ExportConfigCheckboxLabels.standardProfileOptions.includeAnnotations}
        />
      </div>
      <div className="flex flex-row items-center mt-2">
        <FormControlLabel
          control={
            <Checkbox
              checked={!profileExportConfig.skipSupportingRecords}
              onChange={(evt) => setDocumentReferences(evt.target.checked)}
            />
          }
          label={ExportConfigCheckboxLabels.skipSupportingRecords}
        />
        <Tooltip title="The PDF export will include the pinned pages from the original documents.">
          <HelpIconOutlined fontSize="small" />
        </Tooltip>
      </div>
      {!profileExportConfig.skipSupportingRecords && (
        <LabeledBorderBox label={ExportConfigCheckboxLabels.skipSupportingRecords}>
          <div className="flex flex-row items-center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!profileExportConfig.includeAllPages}
                  onChange={(evt) => setIncludeEntireReferenceDocument(evt.target.checked)}
                />
              }
              label={ExportConfigCheckboxLabels.includeAllPages}
            />
            <Tooltip title="Includes the entire document if it contains any pinned pages.">
              <HelpIconOutlined fontSize="small" />
            </Tooltip>
          </div>
        </LabeledBorderBox>
      )}
    </div>
  );
};

const FieldConfig = ({ fieldConfig, formField, fieldTagConfig, tags, profileExportConfigHandle }: FieldConfigProps) => {
  const [_, setProfileExportConfig] = profileExportConfigHandle;

  const selectedCell = formField.cells.find((cell) => cell.id === fieldConfig.valueFilter?.cellId);

  const tagMap = useMemo(() => {
    const tagMap = new Map(tags.map((t) => [t.systemName, t]));
    tagMap.set(STARRED_TAG, STARRED_ENTRY);
    return tagMap;
  }, [tags]);

  const setFieldIncluded = (include: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        if (fieldConfig) {
          fieldConfig.included = include;
        }
      })
    );
  };

  const setShowFieldNotes = (showNotes: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        if (fieldConfig) {
          fieldConfig.skipNotes = !showNotes;
        }
      })
    );
  };

  const setShowSelectedOnly = (selectedOnly: boolean) => {
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        if (fieldConfig) {
          fieldConfig.selectedOnly = selectedOnly;
        }
      })
    );
  };

  const setCellFilter = (f: (existing: FieldExportValueFilter | undefined) => FieldExportValueFilter | undefined) => {
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        if (fieldConfig) {
          fieldConfig.valueFilter = f(fieldConfig.valueFilter ?? undefined);
        }
      })
    );
  };

  const setTagsFilter = (f: (existing: FieldExportTagFilter | undefined) => FieldExportTagFilter | undefined) => {
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        if (fieldConfig) {
          fieldConfig.tagFilter = f(fieldConfig.tagFilter ?? undefined);
        }
      })
    );
  };

  const setFieldContextPageRange = (contextPageRange: number | undefined) => {
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        if (fieldConfig) {
          fieldConfig.contextPageRange = contextPageRange;
        }
      })
    );
  };

  const setFieldLayout = (layout: FieldExportLayout) => {
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        if (fieldConfig) {
          fieldConfig.layout = layout;
        }
      })
    );
  };

  const setFieldCells = (event: SelectChangeEvent<number[]>) => {
    const {
      target: { value },
    } = event;
    setProfileExportConfig(
      produce((draft) => {
        const fieldConfig = (draft.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);
        const valueCellIds = typeof value === "string" ? value.split(",").map((v) => parseInt(v)) : value;

        if (fieldConfig) {
          fieldConfig.cellIds = valueCellIds;
        }
      })
    );
  };

  const isSelectType = (cell: Cell) => cell.type === CellType.Select || cell.type === CellType.MultiSelect;

  const configSkipSupportingRecords = profileExportConfigHandle[0].skipSupportingRecords;
  const configIncludeAllPages = profileExportConfigHandle[0].includeAllPages;

  const contextPagesTooltipTitle =
    !configSkipSupportingRecords && configIncludeAllPages
      ? `Disable "${ExportConfigCheckboxLabels.includeAllPages}" to use Context Pages`
      : configSkipSupportingRecords
        ? `Enable "${ExportConfigCheckboxLabels.skipSupportingRecords}" to use Context Pages`
        : `For each referenced page, export will include the ${fieldConfig.contextPageRange || 0} preceding
      and ${fieldConfig.contextPageRange || 0} following pages.`;

  const cellFilterTooltipTitle = `Include fields where cell value matches ${fieldConfig?.valueFilter?.negated ? "none" : "any"} of the selected options`;

  const tagFilterTooltipTitle = "Include fields tagged with any of the selected options";

  return (
    <Card className="shrink-0">
      <div className="flex flex-col p-2">
        <div className="flex items-center justify-between">
          <P variant="subtitle3">
            {formField?.outlinePrefix} {formField.name}
          </P>
          <Switch checked={fieldConfig.included} onChange={(event) => setFieldIncluded(event.target.checked)} />
        </div>
        {fieldConfig.included && (
          <>
            <div className="flex items-center">
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={fieldConfig.layout === "table"}
                    checked={!fieldConfig.skipNotes && fieldConfig.layout !== "table"}
                    onChange={(event) => setShowFieldNotes(event.target.checked)}
                  />
                }
                label="Include Notes"
              />
              {fieldConfig.layout === "table" && (
                <Tooltip title="Notes Unavailable on Table Layout">
                  <HelpIconOutlined fontSize="small" />
                </Tooltip>
              )}
            </div>
            <div className="flex items-center">
              <FormControlLabel
                label="Table Layout"
                control={
                  <Radio
                    checked={fieldConfig.layout === "table"}
                    onChange={() => setFieldLayout(FieldExportLayout.Table)}
                  />
                }
              />
              <FormControlLabel
                label="Form Layout"
                control={
                  <Radio
                    checked={fieldConfig.layout === "form"}
                    onChange={() => setFieldLayout(FieldExportLayout.Form)}
                  />
                }
              />
            </div>
            <div className="flex items-center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!fieldConfig.selectedOnly}
                    onChange={(event) => setShowSelectedOnly(event.target.checked)}
                  />
                }
                label="Starred Responses Only"
              />
            </div>
            <div className="flex items-center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fieldConfig.contextPageRange !== undefined && fieldConfig.contextPageRange !== null}
                    disabled={configSkipSupportingRecords === true || configIncludeAllPages === true}
                    onChange={(event) => {
                      const radius = event.target.checked ? 1 : undefined;
                      setFieldContextPageRange(radius);
                    }}
                  />
                }
                label="Add Context Pages"
                sx={{ whiteSpace: "nowrap" }}
              />

              {fieldConfig.contextPageRange !== undefined && fieldConfig.contextPageRange !== null && (
                <div className="mr-1">
                  <TextField
                    value={fieldConfig.contextPageRange.toString()}
                    onChange={(evt) => {
                      setFieldContextPageRange(parseSafeInt(evt.target.value) ?? 0);
                    }}
                    label="Number of Pages"
                    disabled={configSkipSupportingRecords === true || configIncludeAllPages === true}
                  />
                </div>
              )}
              <Tooltip title={contextPagesTooltipTitle}>
                <HelpIconOutlined fontSize="small" />
              </Tooltip>
            </div>
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-3 items-center gap-2">
                <FormControl>
                  <InputLabel size="small">Filter Cells</InputLabel>
                  <Select
                    size="small"
                    value={fieldConfig.valueFilter?.cellId.toString() ?? ""}
                    onChange={(event) =>
                      setCellFilter((existing) => ({
                        cellId: +event.target.value,
                        options: [],
                        negated: existing?.negated ?? false,
                      }))
                    }
                    input={<OutlinedInput label="Filter Cells" />}>
                    {formField.cells.filter(isSelectType).map((c) => (
                      <MenuItem value={c.id} key={c.id}>
                        <P>{c.name}</P>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <Select
                    size="small"
                    value={fieldConfig.valueFilter?.negated ? 1 : 0}
                    onChange={(event) =>
                      setCellFilter((existing) =>
                        existing ? { ...existing, negated: !!+event.target.value } : undefined
                      )
                    }>
                    <MenuItem value={0} key={0}>
                      <P>Has Any</P>
                    </MenuItem>
                    <MenuItem value={1} key={1}>
                      <P>Has None</P>
                    </MenuItem>
                  </Select>
                </FormControl>
                <div className="flex items-center justify-between gap-2">
                  <Button
                    disabled={fieldConfig.valueFilter?.cellId === undefined}
                    onClick={(_) => setCellFilter((_) => undefined)}>
                    Clear
                  </Button>
                  <Tooltip title={cellFilterTooltipTitle}>
                    <HelpIconOutlined fontSize="small" />
                  </Tooltip>
                </div>
              </div>
              {selectedCell ? (
                <LabeledBorderBox label="Filter Cells">
                  <Box className="flex flex-col gap-1">
                    {selectedCell.selectOptions?.map((option) => (
                      <FormControlLabel
                        className="ml-5"
                        control={
                          <Checkbox
                            checked={fieldConfig.valueFilter?.options.includes(option)}
                            onChange={(event) =>
                              setCellFilter((existing) =>
                                existing
                                  ? {
                                      ...existing,
                                      options: event.target.checked
                                        ? [...existing.options, option]
                                        : existing.options.filter((o) => o !== option),
                                    }
                                  : undefined
                              )
                            }
                          />
                        }
                        key={option}
                        label={option}
                      />
                    ))}
                  </Box>
                </LabeledBorderBox>
              ) : undefined}
              {(fieldTagConfig?.allowed || []).length > 0 && (
                <div className="flex items-center gap-2">
                  <TagSelect
                    tagMap={tagMap}
                    label="Tags"
                    value={fieldConfig.tagFilter?.tags || []}
                    onChange={(tags) => {
                      setTagsFilter((_) => (tags.length === 0 ? undefined : { tags }));
                      setShowSelectedOnly(tags.includes(STARRED_TAG));
                    }}
                  />
                  <Tooltip title={tagFilterTooltipTitle}>
                    <HelpIconOutlined fontSize="small" />
                  </Tooltip>
                </div>
              )}
              <FormControl>
                <InputLabel size="small">Cells</InputLabel>
                <Select
                  size="small"
                  multiple
                  value={fieldConfig.cellIds || []}
                  onChange={setFieldCells.bind(this)}
                  input={<OutlinedInput label="Cells" />}
                  renderValue={() =>
                    `${formField.cells.length - (fieldConfig.cellIds?.length || 0)} ${
                      formField.cells.length - (fieldConfig.cellIds?.length || 0) === 1 ? "Cell" : "Cells"
                    } Excluded`
                  }>
                  {formField.cells.map((cell) => (
                    <MenuItem key={cell.id} value={cell.id}>
                      <Checkbox checked={fieldConfig.cellIds ? fieldConfig.cellIds.indexOf(cell.id) > -1 : false} />
                      <ListItemText primary={cell.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

const ProfileExportConfigBuilder = ({ lensId, configId }: ProfileExportConfigBuilderProps) => {
  const navigate = useNavigate();
  const formFields = useAppSelector(selectFormFields);
  const fieldTagConfigs = useAppSelector(selectFieldTagConfigs);
  const tags = useAppSelector(selectTags);
  const selectableFormFields = formFields.filter((ff) => !ff.exportExclude);
  const [selectedPreviewCase, setSelectedPreviewCase] = useState<SearchCasesResult | undefined>(undefined);
  const snackpatch = useAppDispatchWithSnackbar();
  const dispatch = useAppDispatch();
  const profileExportConfigs = useAppSelector(selectProfileExportConfigs);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [profileExportConfig, setProfileExportConfig] = useState<ProfileExportConfigUI>(
    newProfileExportConfig(lensId, [])
  );
  const [useCustomTemplate, setUseCustomTemplate] = useState<boolean>(
    profileExportConfig.templateConfig?.fileName !== undefined
  );
  const [templateConfigFile, setTemplateConfigFile] = useState<File | undefined>();
  const [pdfInput, setPdfInput] = useState<{ data: Uint8Array } | undefined>();
  const [pdfNumPages, setPdfNumPages] = useState<number>(1);
  const [showExportPreview, setShowExportPreview] = useState<boolean>(false);
  const [templatePreviewErrorText, setTemplatePreviewErrorText] = useState<string | undefined>();
  const profileExportConfigIsValid =
    profileExportConfig.name.trim() !== "" &&
    !profileExportConfig.fieldConfigs?.every((fc) => !fc.included) &&
    profileExportConfig.fieldConfigs?.every((fc) =>
      fc.included && fc.valueFilter ? fc.valueFilter.options.length > 0 : true
    );
  const [, copy] = useCopy();
  const form = useAppSelector(selectForm);

  const { enqueueSnackbar } = useSnackbar();

  const getCustomTemplateJsonInput = async (caseId: number) => {
    try {
      const r = await runGqlQuery(CaseExportCustomTemplateJsonDocument, {
        caseId: caseId,
        profileExportConfig: profileExportConfigUIToProfileExportConfigInput(profileExportConfig, formFields),
      });
      copy(r.case.caseExportCustomTemplateJson);
    } catch (e) {
      enqueueSnackbar("Error retrieving input", { variant: "error" });
    }
  };

  const updateTemplatePreview = async (
    updatedLensCase: SearchCasesResult,
    file: File | undefined,
    configToSend: ProfileExportConfigInput
  ) => {
    setTemplatePreviewErrorText(undefined);
    if (configToSend.templateConfig?.fileName !== undefined) {
      setShowExportPreview(false);
      const templatePreviewBytes = await backend
        .getTemplatePreview(lensId, updatedLensCase.caseId, configToSend, file)
        .catch((error) => {
          const errorAsArrayBuffer = (error as AxiosError).response?.data as ArrayBuffer;
          const errorAsJson: { message: string; status: number } = JSON.parse(
            new TextDecoder().decode(errorAsArrayBuffer)
          );
          setTemplatePreviewErrorText(errorAsJson.message);
          return undefined;
        });
      if (templatePreviewBytes !== undefined) {
        const templateByteArray = new Uint8Array(templatePreviewBytes);
        const pdfData = {
          data: templateByteArray,
        };
        setPdfInput(pdfData);
        // We don't show the export preview here, because we want to defer it until the PDF actually loads.
      } else {
        setPdfInput(undefined);
        setShowExportPreview(true);
      }
    }
  };

  const selectConfig = async (configId?: number) => {
    if (configId === undefined) {
      const newConfig = newProfileExportConfig(lensId, formFields);
      setProfileExportConfig(newConfig);
      setUseCustomTemplate(false);
    } else {
      const foundProfileExportConfig = profileExportConfigs.find((pec) => pec.id === configId);
      if (foundProfileExportConfig) {
        setProfileExportConfig(profileExportConfigViewToProfileExportConfigUI(foundProfileExportConfig, formFields));
        setUseCustomTemplate(foundProfileExportConfig.config.templateConfig?.fileName !== undefined);
      }
    }
    setTemplateConfigFile(undefined);
    setTemplatePreviewErrorText(undefined);
    navigate(AppRoutes.lensExportConfigBuilder(lensId, configId));
  };

  const deleteConfig = (name: string, configId: number) => {
    confirmCtx.confirm({
      title: "Delete Profile Export Config",
      prompt: <P variant="body4">Are you sure you want to delete {name}?</P>,
      callback: () => {
        return snackpatch(deleteProfileExportConfig({ configId }), {
          successMessage: "Profile Export Config Deleted",
          errorMessage: "Unable to delete Profile Export Config",
        }).then(() => selectConfig(undefined));
      },
    });
  };

  const saveConfig = async () => {
    const exportConfig = profileExportConfigUIToProfileExportConfigInput(profileExportConfig, formFields);
    try {
      const profileExportViewConfig = await dispatch(
        configId === undefined
          ? createProfileExportConfig({
              lensId,
              name: profileExportConfig.name,
              exportConfig,
            })
          : updateProfileExportConfig({
              lensId,
              name: profileExportConfig.name,
              configId,
              exportConfig,
            })
      ).unwrap();
      if (templateConfigFile) {
        await backend.uploadProfileExportTemplate(
          templateConfigFile,
          templateConfigFile.name,
          lensId,
          profileExportViewConfig.id
        );
      }
      enqueueSnackbar("Config saved", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Unable to save config", { variant: "error" });
    }
  };

  useEffect(() => {
    let config = newProfileExportConfig(lensId, formFields);
    if (configId) {
      const foundProfileExportConfig = profileExportConfigs.find((pec) => pec.id === configId);
      if (foundProfileExportConfig) {
        config = profileExportConfigViewToProfileExportConfigUI(foundProfileExportConfig, formFields);
      }
    }
    setProfileExportConfig(config);
  }, [formFields, profileExportConfigs]);

  useEffect(() => {
    void dispatch(fetchForm({ lensId }));
  }, [lensId]);

  useEffect(() => {
    if (profileExportConfig.templateConfig?.fileName === undefined) {
      setShowExportPreview(false);
      setTemplatePreviewErrorText(undefined);
      setPdfInput(undefined);
      setShowExportPreview(true);
    } else if (selectedPreviewCase) {
      updateTemplatePreview(
        selectedPreviewCase,
        templateConfigFile,
        profileExportConfigUIToProfileExportConfigInput(profileExportConfig, formFields)
      ).catch((err) => {
        console.error(err);
        setPdfInput(undefined);
        setShowExportPreview(true);
      });
    }
  }, [selectedPreviewCase, profileExportConfig.templateConfig]);

  return (
    <Box sx={{ flex: 1, minHeight: 150, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "400px 1fr",
          gap: "16px 16px",
          minHeight: 0,
          height: "100%",
        }}>
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, mt: 1 }}>
            <FormControl fullWidth>
              <Select
                value={configId}
                size="small"
                onChange={(event) => selectConfig(event.target.value as number)}
                displayEmpty>
                <MenuItem value={undefined}>New Config</MenuItem>
                {profileExportConfigs.map((pec) => (
                  <MenuItem key={pec.id} value={pec.id}>
                    {pec.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              borderRadius: 1,
              px: 2,
              backgroundColor: "white",
            }}>
            <CollapsibleSections
              key={configId}
              sections={[
                {
                  title: (
                    <Box sx={{ py: 2 }}>
                      <P variant="xlBold">Export Settings</P>
                    </Box>
                  ),
                  items: [
                    <ProfileConfig
                      key="profile-config"
                      profileExportConfigHandle={[profileExportConfig, setProfileExportConfig]}
                      templateConfigFileHandle={[templateConfigFile, setTemplateConfigFile]}
                      useCustomTemplateHandle={[useCustomTemplate, setUseCustomTemplate]}
                    />,
                  ],
                  expanded: true,
                },
              ]}
            />
          </Box>

          <P
            variant="xlBold"
            sx={{
              mt: 2,
              mb: 1,
            }}>
            Fields
          </P>
          <Box sx={{ overflow: "auto", display: "flex", flexDirection: "column", minHeight: 100, gap: 1 }}>
            {selectableFormFields.map((formField) => {
              const fieldConfig = (profileExportConfig.fieldConfigs || []).find((fC) => fC.fieldId === formField.id);

              return fieldConfig ? (
                <FieldConfig
                  profileExportConfigHandle={[profileExportConfig, setProfileExportConfig]}
                  key={formField.id}
                  fieldConfig={fieldConfig}
                  formField={formField}
                  fieldTagConfig={fieldTagConfigs.find((fieldTagConfig) => fieldTagConfig.fieldId === formField.id)}
                  tags={tags}
                />
              ) : (
                <Fragment key={formField.id}></Fragment>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", mt: 2 }}>
            {configId && (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => deleteConfig(profileExportConfig.name, configId)}>
                Delete
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button color="primary" variant="outlined" sx={{ mr: 2 }} onClick={() => selectConfig(configId)}>
              Reset
            </Button>
            <Tooltip
              title={
                !profileExportConfigIsValid
                  ? "Export Configuration name is required, at least one field must be enabled, and cell filter options must be present if enabled"
                  : ""
              }
              arrow>
              <span>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => saveConfig()}
                  disabled={!profileExportConfigIsValid}>
                  Save
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            minHeight: 0,
            height: "100%",
            overflow: "auto",
          }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, mt: 1 }}>
            <P variant="xlBold">Preview</P>
            <FormControl sx={{ width: "300px", ml: 2 }}>
              <CaseAutocomplete
                label="Search for a case"
                selected={undefined}
                onChange={setSelectedPreviewCase}
                lensId={lensId}
              />
            </FormControl>

            {profileExportConfig.templateConfig?.fileName !== undefined && selectedPreviewCase !== undefined && (
              <Tooltip title="Refresh Custom Template Preview">
                <LoadingButton
                  color="default"
                  onClick={async () => {
                    await updateTemplatePreview(
                      selectedPreviewCase,
                      templateConfigFile,
                      profileExportConfigUIToProfileExportConfigInput(profileExportConfig, formFields)
                    );
                  }}>
                  <RefreshIcon color="inherit" />
                </LoadingButton>
              </Tooltip>
            )}

            {selectedPreviewCase !== undefined && profileExportConfig.templateConfig?.fileName !== undefined && (
              <Tooltip title="Copy Template Config JSON">
                <IconButton
                  size="small"
                  sx={{ color: "secondary" }}
                  onClick={() => getCustomTemplateJsonInput(selectedPreviewCase.caseId)}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {selectedPreviewCase !== undefined && form !== undefined && (
            <CaseFormProvider
              key={selectedPreviewCase.caseId}
              lensId={lensId}
              caseId={selectedPreviewCase.caseId}
              form={form}>
              {templatePreviewErrorText !== undefined && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {templatePreviewErrorText.split("\n").map((value, index) => (
                    <P key={index} sx={{ whiteSpace: "pre-wrap", fontFamily: "Courier" }}>
                      {value}
                    </P>
                  ))}
                </Alert>
              )}
              {pdfInput !== undefined && (
                <Document
                  file={pdfInput}
                  loading=""
                  onLoadError={() => setShowExportPreview(true)}
                  onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}>
                  {[...Array(pdfNumPages)].map((_x, page) => (
                    <>
                      <Page
                        key={page}
                        loading=""
                        onLoadError={() => setShowExportPreview(true)}
                        onLoadSuccess={() => setShowExportPreview(true)}
                        pageNumber={page + 1}
                      />
                      <hr />
                    </>
                  ))}
                </Document>
              )}
              {showExportPreview && !profileExportConfig.skipCoverPages && (
                <ProfileExportConfigPreview caseData={selectedPreviewCase} profileExportConfig={profileExportConfig} />
              )}
            </CaseFormProvider>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileExportConfigBuilder;
